import { useMemo } from 'react';
import { FilesResponseType, TreeItemType } from '@api';
import { useFilesMutations, useFileTree } from '@common';
import { TreeInstance } from '@headless-tree/core';

type UseTreeMethodsArgsType = {
  files: FilesResponseType;
  isFetchingTreeItems: boolean;
  refetchFiles: () => void;
  isInitiallyExpanded: boolean;
};

type UseTreeMethodsReturnType = {
  rootFolderId: string;
  tree: TreeInstance<TreeItemType>;
  isFetching: boolean;
  onDeleteItem: (item: TreeItemType) => void;
};

export const useTreeMethods = ({
  files,
  isFetchingTreeItems,
  refetchFiles,
  isInitiallyExpanded,
}: UseTreeMethodsArgsType): UseTreeMethodsReturnType => {
  const { moveFile, renameFile, deleteFile } = useFilesMutations(refetchFiles);

  const memoizedFiles = useMemo(() => files, [files]);

  const rootFolderId = 'root';

  const expandedItems = isInitiallyExpanded
    ? Object.keys(memoizedFiles)
    : [rootFolderId];

  const tree = useFileTree<TreeItemType>({
    files: memoizedFiles,
    onRenameItem: renameFile,
    onMoveItem: moveFile,
    seperateDragHandle: true,
    initialState: { expandedItems },
    setDragImage: (draggedItems) => ({
      imgElement: document.getElementById(draggedItems[0].getId())!,
    }),
  });

  return {
    rootFolderId,
    tree,
    isFetching: isFetchingTreeItems,
    onDeleteItem: deleteFile,
  };
};
